/**
 * Feature flags for deferred channels (Phase 56.5.1).
 *
 * Gated by VITE_FEATURE_* env vars. Undefined → false.
 * Default-off so each channel is hidden until activated; flip to
 * 'true' in Vercel env vars + redeploy when activation conditions
 * are met:
 *   - WhatsApp: requires Twilio WhatsApp sender provisioned
 *   - Messenger: requires Meta App Review approval for pages_messaging
 *   - Instagram: requires same Meta App approval + IG Business linkage
 *   - metaLeadgen: requires the dedicated Meta Lead Ads app review
 *     (leads_retrieval + ads_management + ads_read + business_management
 *     + pages_manage_ads + pages_read_engagement + pages_show_list)
 *
 * Consumers:
 *   - src/lib/navConfig.ts: gates inbox + admin nav entries
 *   - src/App.tsx: gates route registration
 *   - src/components/inbox/ThreadList.tsx: gates ChannelFilterPills
 *     source so disabled-channel conversations don't surface in the
 *     all-channels inbox view
 *
 * Activation playbook:
 *   1. Set VITE_FEATURE_<CHANNEL>=true in Vercel env vars (production scope)
 *   2. Trigger redeploy via Vercel dashboard or `vercel --prod`
 *   3. Nav entry + route + inbox filter all light up automatically
 *
 * Local dev: drop VITE_FEATURE_META_LEADGEN=true into .env.local to
 * surface the page only on your machine without exposing it in prod.
 *
 * Page components stay imported in App.tsx regardless — keeping them
 * in the bundle means a one-line env-var flip is the entire activation.
 */
export const features = {
  whatsapp: import.meta.env.VITE_FEATURE_WHATSAPP === 'true',
  messenger: import.meta.env.VITE_FEATURE_MESSENGER === 'true',
  instagram: import.meta.env.VITE_FEATURE_INSTAGRAM === 'true',
  metaLeadgen: import.meta.env.VITE_FEATURE_META_LEADGEN === 'true',
  // Wave 19: Google Ads Lead Forms (per-merchant webhook, no OAuth).
  googleLeadAds: import.meta.env.VITE_FEATURE_GOOGLE_LEAD_ADS === 'true',
  // Sites / AI website builder. SITES-2 W1: default ON for production
  // builds (set VITE_FEATURE_SITES=false to kill-switch it off). The nav
  // entry stays platform-admin only until the cutover wave; the flag
  // gates routes + nav together.
  sites: import.meta.env.VITE_FEATURE_SITES !== 'false',
  // SITES-COEXIST-1: the ONE cutover switch. While false, the Sites builder
  // stays platform-admin only (soft launch). Flip VITE_FEATURE_SITES_GA=true
  // (or this default) in one place to expose the Sites builder to EVERY
  // merchant with marketing.page_edit — no scattered isAdmin checks to hunt.
  sitesGA: import.meta.env.VITE_FEATURE_SITES_GA === 'true',
} as const

/** The single gate for the Sites builder's visibility to a merchant. While
 *  `sitesGA` is false only platform admins see it; flipping `sitesGA` exposes
 *  it to all merchants. Kept here so the cutover decision is one line. */
export function sitesBuilderVisible(isAdmin: boolean): boolean {
  return features.sites && (features.sitesGA || isAdmin)
}
